import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Member, EnrollmentStats } from '../models/member.model';
import { MemberService } from '../services/member.service';
import { Users, UserCheck, CreditCard, Activity } from 'lucide-react';

export const EnrollmentDashboard: React.FC = () => {
    const [members, setMembers] = useState<Member[]>([]);
    const [stats, setStats] = useState<EnrollmentStats | null>(null);
    const [loading, setLoading] = useState(true);

    const navigate = useNavigate();

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [membersData, statsData] = await Promise.all([
                MemberService.getAllMembers(),
                MemberService.getEnrollmentStats(),
            ]);
            setMembers(membersData);
            setStats(statsData);
        } catch (error) {
            console.error('Error loading enrollment data:', error);
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (member: Member) => {
        if (member.isActive) {
            return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">Active</span>;
        }
        if (member.userId) {
            return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">Account Created</span>;
        }
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">Invited</span>;
    };

    const PipelineStep = ({ label, count, total, color, icon: Icon }: any) => {
        const percentage = total > 0 ? (count / total) * 100 : 0;
        const colorClasses = {
            purple: 'bg-purple-50 text-purple-600',
            blue: 'bg-blue-50 text-blue-600',
            green: 'bg-green-50 text-green-600',
            teal: 'bg-teal-50 text-teal-600',
        };
        const barColors = {
            purple: 'bg-purple-500',
            blue: 'bg-blue-500',
            green: 'bg-green-500',
            teal: 'bg-teal-500',
        };

        return (
            <div className="bg-white rounded-lg shadow p-6 flex-1">
                <div className="flex items-center mb-4">
                    <div className={`p-2 rounded-lg mr-3 ${colorClasses[color as keyof typeof colorClasses]}`}>
                        <Icon className="w-6 h-6" />
                    </div>
                    <span className="font-semibold text-gray-600">{label}</span>
                </div>
                <div className="mb-2">
                    <span className="text-3xl font-bold">{count}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                    <div
                        className={`${barColors[color as keyof typeof barColors]} h-2 rounded-full`}
                        style={{ width: `${percentage}%` }}
                    ></div>
                </div>
                <p className="text-xs text-gray-500">{percentage.toFixed(1)}% conversion</p>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="p-8 flex justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="p-8 max-w-7xl mx-auto">
            <h1 className="text-2xl font-bold mb-6 text-gray-900">Member Onboarding & Enrollment Pipeline</h1>

            {/* Pipeline View */}
            <div className="mb-8">
                <h2 className="text-lg font-medium text-gray-600 mb-4">Enrollment Funnel</h2>
                <div className="flex flex-col md:flex-row gap-4">
                    <PipelineStep
                        label="Total Invited"
                        count={stats?.totalInvited || 0}
                        total={stats?.totalInvited || 1}
                        color="purple"
                        icon={Users}
                    />
                    <PipelineStep
                        label="Account Created"
                        count={members.filter(m => m.userId).length}
                        total={stats?.totalInvited || 1}
                        color="blue"
                        icon={UserCheck}
                    />
                    <PipelineStep
                        label="Active Users"
                        count={stats?.activeUsers || 0}
                        total={stats?.totalInvited || 1}
                        color="green"
                        icon={Activity}
                    />
                    <PipelineStep
                        label="Bank Connected"
                        count={stats?.bankConnected || 0}
                        total={stats?.activeUsers || 1}
                        color="teal"
                        icon={CreditCard}
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-medium mb-4">Onboarding Rate</h3>
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-3xl font-bold text-gray-900">
                                {stats?.onboardingRate.toFixed(1)}%
                            </p>
                            <p className="text-gray-500">Invited to Active</p>
                        </div>
                        <div className="w-24 h-24 relative flex items-center justify-center">
                            <svg className="w-full h-full" viewBox="0 0 36 36">
                                <path
                                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                    fill="none"
                                    stroke="#E5E7EB"
                                    strokeWidth="3"
                                />
                                <path
                                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                    fill="none"
                                    stroke="#22C55E"
                                    strokeWidth="3"
                                    strokeDasharray={`${stats?.onboardingRate}, 100`}
                                />
                            </svg>
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-medium mb-4">Bank Connection Rate</h3>
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-3xl font-bold text-gray-900">
                                {stats?.bankConnectionRate.toFixed(1)}%
                            </p>
                            <p className="text-gray-500">Active to Bank Connected</p>
                        </div>
                        <div className="w-24 h-24 relative flex items-center justify-center">
                            <svg className="w-full h-full" viewBox="0 0 36 36">
                                <path
                                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                    fill="none"
                                    stroke="#E5E7EB"
                                    strokeWidth="3"
                                />
                                <path
                                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                    fill="none"
                                    stroke="#14B8A6"
                                    strokeWidth="3"
                                    strokeDasharray={`${stats?.bankConnectionRate}, 100`}
                                />
                            </svg>
                        </div>
                    </div>
                </div>
            </div>

            {/* Member List */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">Recent Members</h3>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Enrollment Date</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bank Status</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {members.slice(0, 10).map((member) => (
                                <tr
                                    key={member.id}
                                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                                    onClick={() => navigate(`/enrollment/${member.id}`)}
                                >
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600 hover:underline">
                                        {member.firstName} {member.lastName}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{member.email}</td>
                                    <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(member)}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {new Date(member.enrollmentDate).toLocaleDateString()}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {member.bankAccountVerified ? (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                                                Verified
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                                Not Connected
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};
